import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { FlowNode, ActionData } from '../../types';
import { Icon, type IconName } from '../Icon';
import { NodeIssueBadge } from './NodeIssueBadge';

const ACTION_ICON: Record<string, IconName> = {
  'sms.send':            'sms',
  'email.send':          'mail',
  'task.create':         'task',
  'workflow.notify_rep': 'bell',
  'webhook.post':        'webhook',
  'log.audit':           'audit',
};

function ActionNodeImpl({ data, selected, id }: NodeProps<FlowNode>) {
  const d = data as ActionData;
  return (
    <div className={`vnode act ${selected ? 'selected' : ''}`} data-node-id={id}>
      <NodeIssueBadge issues={(d as any).__issues} />
      <Handle type="target" position={Position.Top} id="in" />
      <div className="vnode-head">
        <span className="vnode-lbl">
          <Icon name={ACTION_ICON[d.type] || 'play'} size={11} variant="green" />
          Action · then do this
        </span>
      </div>

      {d.type === 'sms.send' && <SmsSendPreview body={d.body} to={d.to} label={d.label} />}
      {d.type === 'email.send' && <EmailSendPreview body={d.body} to={d.to} label={d.label} />}
      {d.type === 'task.create' && <TaskPreview label={d.label} description={d.description || d.body} />}
      {d.type === 'workflow.notify_rep' && <NotifyRepPreview label={d.label} description={d.description || d.body} />}
      {d.type === 'webhook.post' && <WebhookPreview label={d.label} to={d.to} />}
      {d.type === 'log.audit' && <AuditLogPreview label={d.label} description={d.description || d.body} />}
    </div>
  );
}

export const ActionNode = memo(ActionNodeImpl);

/* ---------- visual cards ---------- */

function SmsSendPreview({ body, to, label }: { body?: string; to?: string; label: string }) {
  return (
    <div className="vcard vcard-action-sms">
      <div className="vcard-sms-bubble out">
        {body ? (body.length > 110 ? body.slice(0, 107) + '…' : body) : 'SMS body…'}
      </div>
      <div className="vcard-sms-foot">
        <span className="vcard-sms-icon">☏</span>
        <span><b style={{ color: 'var(--ink)' }}>{label}</b> · to <code className="vcard-mtag">{to || '{{customer.phone}}'}</code></span>
      </div>
    </div>
  );
}

function EmailSendPreview({ body, to, label }: { body?: string; to?: string; label: string }) {
  return (
    <div className="vcard vcard-action-email">
      <div className="vcard-email-head">
        <span className="vcard-email-from">From: jalen@impactiq.us</span>
        <span className="vcard-email-to">To: <code className="vcard-mtag">{to || '{{customer.email}}'}</code></span>
      </div>
      <div className="vcard-email-body">
        {body ? (body.length > 110 ? body.slice(0, 107) + '…' : body) : 'Email body…'}
      </div>
      <div className="vcard-sms-foot">
        <span className="vcard-sms-icon">@</span>
        <b style={{ color: 'var(--ink)' }}>{label}</b>
      </div>
    </div>
  );
}

function TaskPreview({ label, description }: { label: string; description?: string }) {
  return (
    <div className="vcard vcard-action-task">
      <div className="vcard-task-head">
        <span className="vcard-task-check">☐</span>
        <b>{label}</b>
      </div>
      {description && <span className="vcard-meta">{description}</span>}
      <div className="vcard-task-meta">
        <span>Assigned: <b style={{ color: 'var(--gold-light)' }}>auto-route</b></span>
        <span>Due: <b style={{ color: 'var(--gold-light)' }}>+1 day</b></span>
      </div>
    </div>
  );
}

function NotifyRepPreview({ label, description }: { label: string; description?: string }) {
  return (
    <div className="vcard vcard-action-notify">
      <div className="vcard-notify-row">
        <div className="vcard-rep-av">JW</div>
        <div>
          <b>{label}</b>
          {description && <span className="vcard-meta">{description}</span>}
        </div>
        <span className="vcard-notify-badge">●</span>
      </div>
    </div>
  );
}

function WebhookPreview({ label, to }: { label: string; to?: string }) {
  return (
    <div className="vcard vcard-action-webhook">
      <div className="vcard-webhook-row">
        <span className="vcard-method">POST</span>
        <code className="vcard-url">{to || 'https://your-endpoint.com/hook'}</code>
      </div>
      <b style={{ color: 'var(--ink)', fontSize: 12.5 }}>{label}</b>
    </div>
  );
}

function AuditLogPreview({ label, description }: { label: string; description?: string }) {
  return (
    <div className="vcard vcard-action-audit">
      <div className="vcard-audit-row">
        <span className="vcard-audit-ic">▤</span>
        <div>
          <b>{label}</b>
          <span className="vcard-meta">{description || 'Compliance log only · no SMS sent'}</span>
        </div>
      </div>
    </div>
  );
}
